import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconTrash } from '@components/icons/trash';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useDeleteJourney } from 'api/journeys/hooks';
import { JourneyType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';

export const useDeleteJourneyModal = () => useModal(DeleteJourney);

export const DeleteJourney = ({ journey }: { journey: JourneyType }) => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);

  const onSuccess = async () => {
    hideModal();
    navigate(generatePath(routePaths.workspaceJourneyList, { workspaceId: journey.workspace }), {
      replace: true,
    });
  };
  const deleteMutation = useDeleteJourney({
    id: journey.shortId,
    onSuccess,
  });

  return (
    <Modal>
      <Modal.SimpleHeader title={t('deleteJourney')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack>
            <Text text={t('areYouSure', journey.title)} />
            <Text text={t('allDataLost')} />
            <Text color="elementRed100" text={t('cannotBeUndone')} />
          </Stack>
          <Separator />
          <Stack space={4}>
            <Text font="semibold14" text={t('pleaseConfirm')} />
            <Checkbox
              dataCy="confirm-delete-journey"
              label={t('iUnderstand')}
              onChange={(value) => setConfirm(value)}
            />
          </Stack>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              data-cy="delete-journey"
              isDisabled={!confirm}
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isLoading}
              variant="primary-red"
              iconLeft={<IconTrash />}
              text={t('core.delete')}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    deleteJourney: 'Delete journey',
    areYouSure: 'Are you sure you want to delete the journey {0}?',
    allDataLost: 'All the journey and traveller details and process will be lost',
    cannotBeUndone: 'This can not be undone.',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand',
  },
};
